import React from 'react';
import { Box, Typography, Theme } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ChevronRight } from '@material-ui/icons';
import { CircularProgressOverlay } from '@campfire/circular-progress-overlay';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { ChartDataPoint, FormSubmissionListItem } from './form-responses-types';
import { formatSubmissionDate } from './form-responses-utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sectionTitle: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: theme.color.grey.neutral400,
      letterSpacing: '-0.01em',
    },
    statCard: {
      backgroundColor: '#fff',
      border: `1px solid ${theme.color.grey.neutralBrand200}`,
      borderRadius: '8px',
      padding: '16px 24px',
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: 700,
      color: theme.color.grey.neutral400,
      letterSpacing: '-0.02em',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: theme.color.grey.neutral300,
    },
    chartContainer: {
      backgroundColor: '#fff',
      border: `1px solid ${theme.color.grey.neutralBrand200}`,
      borderRadius: '8px',
      padding: '16px 24px',
      height: '160px',
      display: 'flex',
      flexDirection: 'column',
    },
    emptyState: {
      backgroundColor: theme.color.grey.neutralBrand100,
      borderRadius: '8px',
      padding: '48px',
      textAlign: 'center',
    },
    emptyStateTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: theme.color.grey.neutral300,
      marginBottom: '8px',
    },
    emptyStateText: {
      fontSize: '0.875rem',
      color: theme.color.grey.neutral300,
      maxWidth: '400px',
      margin: '0 auto',
    },
    recentResponseItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 16px',
      borderBottom: `1px solid ${theme.color.grey.neutralBrand100}`,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.color.grey.neutralBrand100,
      },
      '&:last-child': {
        borderBottom: 'none',
      },
    },
  })
);

interface SummaryTabProps {
  submissionCount: number;
  summaryLoading: boolean;
  chartData: ChartDataPoint[];
  recentSubmissions: FormSubmissionListItem[];
  onViewSubmission: (index: number, submissionId: string) => void;
}

export const SummaryTab: React.FC<SummaryTabProps> = ({
  submissionCount,
  summaryLoading,
  chartData,
  recentSubmissions,
  onViewSubmission,
}) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' marginBottom={3}>
        <Typography className={classes.sectionTitle}>Response Summary</Typography>
      </Box>

      {submissionCount === 0 ? (
        <Box className={classes.emptyState}>
          <Typography className={classes.emptyStateTitle}>No responses yet</Typography>
          <Typography className={classes.emptyStateText}>
            Share your form to start collecting submissions. Response statistics will appear here.
          </Typography>
        </Box>
      ) : summaryLoading ? (
        <CircularProgressOverlay isLoading />
      ) : (
        <Box>
          <Box display='flex' style={{ gap: '24px', marginBottom: '24px' }}>
            <Box className={classes.statCard} style={{ flex: '0 0 200px' }}>
              <Typography className={classes.statValue}>{submissionCount}</Typography>
              <Typography className={classes.statLabel}>Total responses</Typography>
            </Box>
            <Box className={classes.chartContainer} style={{ flex: 1 }}>
              <Typography className={classes.sectionTitle} style={{ marginBottom: '8px' }}>
                Responses over time
              </Typography>
              {chartData.length > 0 ? (
                <Box style={{ flex: 1, minHeight: 0 }}>
                  <ResponsiveContainer width='100%' height='100%'>
                    <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray='3 3' stroke='#eee' />
                      <XAxis dataKey='date' tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip
                        formatter={(value: number) => [value, 'Responses']}
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: `1px solid ${theme.color.grey.neutralBrand200}`,
                          borderRadius: '6px',
                          padding: '6px 10px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          fontSize: '0.75rem',
                        }}
                        labelStyle={{ fontWeight: 500, marginBottom: '2px', fontSize: '0.75rem' }}
                        itemStyle={{ fontSize: '0.75rem', padding: 0 }}
                      />
                      <Area
                        type='monotone'
                        dataKey='responses'
                        stroke={theme.palette.primary.main}
                        fill={theme.palette.primary.light}
                        fillOpacity={0.3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography className={classes.statLabel}>No data available</Typography>
              )}
            </Box>
          </Box>

          <Box className={classes.statCard}>
            <Typography className={classes.sectionTitle} style={{ marginBottom: '16px' }}>
              Recent responses
            </Typography>
            {recentSubmissions.length > 0 ? (
              <Box>
                {recentSubmissions.map((submission, index) => (
                  <Box
                    key={submission.formSubmissionId}
                    className={classes.recentResponseItem}
                    onClick={() => onViewSubmission(index, submission.formSubmissionId)}
                  >
                    <Box>
                      <Typography style={{ fontSize: '0.875rem', fontWeight: 500 }}>Response #{index + 1}</Typography>
                      <Typography className={classes.statLabel}>
                        {formatSubmissionDate(submission.submissionDate)}
                      </Typography>
                    </Box>
                    <ChevronRight style={{ color: theme.color.grey.neutral300 }} />
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography className={classes.statLabel}>No recent responses</Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
